import React from "react";
import { makeClassName, Row, StyleProps } from "@hex-insights/core";
import { BooleanFieldInputProps } from "@hex-insights/forms";
import styles from "./styles.module.css";

// TODO add to hex core (switch with optional labels)

export type BooleanSwitchInputProps = BooleanFieldInputProps & {
	trueLabel?: string;
	falseLabel?: string;
} & Partial<StyleProps>;

export function BooleanSwitchInput({
	id,
	name,
	value,
	setValue,
	onFocus,
	onBlur,
	autoFocus,
	disabled,
	trueLabel = "Yes",
	falseLabel = "No",
	className,
	style,
}: BooleanSwitchInputProps) {
	const inputRef = React.useRef<HTMLInputElement | null>(null);

	const onChange = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setValue(event.target.checked);
		},
		[setValue],
	);

	const focusInput = React.useCallback(() => {
		if (inputRef.current !== null) {
			inputRef.current.focus();
		}
	}, []);

	const onClickTrue = React.useCallback(() => {
		setValue(true);
		focusInput();
	}, [setValue, focusInput]);

	const onClickFalse = React.useCallback(() => {
		setValue(false);
		focusInput();
	}, [setValue, focusInput]);

	return (
		<Row
			justify="spaced-start"
			align="center"
			horizontalSpacing="0.5rem"
			className={makeClassName("no-select", className)}
			style={style}
		>
			<span
				onClick={onClickFalse}
				title={falseLabel}
				className={makeClassName(styles["label"], styles["label--false"], !value ? styles["label--selected"] : "")}
			>
				{falseLabel}
			</span>

			<label>
				<div className={makeClassName(styles["switch"], value ? styles["switch--on"] : styles["switch--off"])}>
					<div className={styles["switch__ball"]}></div>
				</div>

				<input
					type="checkbox"
					ref={inputRef}
					id={id ?? name}
					name={name}
					checked={value}
					onChange={onChange}
					onClick={focusInput}
					onFocus={onFocus}
					onBlur={onBlur}
					autoFocus={autoFocus}
					disabled={disabled}
					className={styles["input"]}
				/>
			</label>

			<span
				onClick={onClickTrue}
				title={trueLabel}
				className={makeClassName(styles["label"], styles["label--true"], value ? styles["label--selected"] : "")}
			>
				{trueLabel}
			</span>
		</Row>
	);
}
